import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { CitationsSection } from '@terminal/components/citations-section/CitationsSection';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { TextSection } from '@terminal/components/text-section/TextSection';
import { ImpactedCompanies } from '@terminal/modules/impacted-companies/ImpactedCompanies';
import { ImpactedCountriesAndRegions } from '@terminal/modules/impacted-countries-and-regions/ImpactedCountriesAndRegions';
import { ThematicFocus } from '@terminal/modules/thematic-focus/ThematicFocus';
import { useCatalystInsightDetails } from '@terminal/react-query-hooks/insights/useCatalystInsights';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { CatalystDrawerHeader } from '@terminal/widgets/catalyst-insights-drawer/catalyst-drawer-header/CatalystDrawerHeader';
import { splitSectionGroups } from '@terminal/widgets/catalyst-insights-drawer/utils';
import { useTranslation } from 'react-i18next';

import styles from './MarketCatalystWidget.module.scss';

export interface MarketCatalystWidgetProps {
  insightId: string;
  expandCitations: boolean;
}

export const MarketCatalystWidget = ({
  insightId,
  expandCitations = false,
}: MarketCatalystWidgetProps) => {
  const { t } = useTranslation(['insights']);
  const {
    data: catalystInsight,
    isLoading,
    error,
  } = useCatalystInsightDetails(insightId, !!insightId);

  if (error) {
    throw error;
  }

  if (isLoading) {
    return (
      <SkeletonLoader
        className={styles.loadingSkeletonWrapper}
        areas={[
          {
            styles: {
              width: '100%',
              height: '64px',
              borderRadius: '8px',
            },
          },
          {
            styles: {
              width: '100%',
              height: '136px',
              borderRadius: '8px',
            },
          },
          {
            styles: {
              width: '100%',
              height: '400px',
              borderRadius: '8px',
            },
          },
          {
            styles: {
              width: '100%',
              height: '400px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (!catalystInsight) {
    return null;
  }

  const { firstSectionGroup, otherSectionGroups } = splitSectionGroups(
    catalystInsight.section_groups
  );

  return (
    <ErrorBoundaryWrapper>
      <ErrorBoundaryWrapper>
        <CatalystDrawerHeader
          identifier={catalystInsight.identifier}
          catalystInsight={catalystInsight}
        />
      </ErrorBoundaryWrapper>
      <div className={styles.sectionGroupsWrapper}>
        {firstSectionGroup.map((group, gi) => (
          <div
            key={`${group.title}-${gi}`}
            className={styles.groupWrapper}
            data-testid="first-section-group-wrapper"
          >
            <div className={styles.sectionGroupTitle}>{group.title}</div>
            {group.sections.map((section, si) => (
              <TextSection
                key={`${section.title}-${si}`}
                title={section.title}
                textContent={section.content ?? []}
                variant="secondary"
                useBulletPoints
              />
            ))}
          </div>
        ))}
        <div>
          <ThematicFocus
            themes={catalystInsight.themes}
            otherThemes={catalystInsight.other_themes}
            title={t('insights:catalyst.thematicFocus')}
            source={POSTHOG_TRACKING_SOURCES.MARKET_CATALYST_DRAWER}
            withPill
          />
        </div>
        <ImpactedCountriesAndRegions
          impactedCountries={catalystInsight.impacted_countries}
          impactedRegions={catalystInsight.impacted_regions}
          title={t('insights:catalyst.mostImpactedRegions')}
        />
        <ImpactedCompanies
          impactedCompanies={catalystInsight.impacted_companies}
          source={POSTHOG_TRACKING_SOURCES.MARKET_CATALYST_DRAWER}
          variant="secondary"
          title={t('insights:catalyst.mostImpactedCompanies')}
          insightCreatedAt={catalystInsight.created_at}
          hideRelationship
        />
        {otherSectionGroups.map((group, gi) => (
          <div
            key={`${group.title}-${gi}`}
            className={styles.groupWrapper}
            data-testid="other-section-group-wrapper"
          >
            <div className={styles.sectionGroupTitle}>{group.title}</div>
            {group.sections.map((section, si) => (
              <TextSection
                key={`${section.title}-${si}`}
                title={section.title}
                textContent={section.content ?? []}
                variant="secondary"
                useBulletPoints
              />
            ))}
          </div>
        ))}
        {!!catalystInsight.citations.length && (
          <div className={styles.citationsSectionWrapper}>
            <div className={styles.citationsTitle}>
              {t('insights:catalyst.citations')}
            </div>
            <CitationsSection
              count={catalystInsight.citations.length}
              items={catalystInsight.citations}
              title={t('insights:catalyst.webLinks')}
              isExpanded={expandCitations}
            />
          </div>
        )}
      </div>
    </ErrorBoundaryWrapper>
  );
};
